"use client"

import { useEffect } from "react"
import { TriangleAlert } from "lucide-react"
import { useTranslations } from "next-intl"

import { notifyBoundaryError } from "@/lib/bugsnag/notify"
import { recoverFromDeploySkew } from "@/lib/errors/deploy-skew"

/**
 * Storefront error boundary — catches runtime errors in storefront pages
 * (product, category, search, homepage). Renders within (storefront)/layout.tsx
 * so the header and footer remain visible; only the page content is replaced.
 *
 * MUST be a "use client" component — Next.js requirement for error.tsx.
 *
 * FM2: SVG path is a static string resolved at build time, not a dynamic import,
 * to ensure it loads even during chunk loading failures.
 *
 * FM3: If this error boundary itself fails to render (e.g., translation loading
 * failure), Next.js will bubble to the root error boundary. The component never
 * exposes error.message or stack traces to the shopper.
 */
export default function StorefrontError({
  error,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Deploy-skew (stale chunk / Server Action) self-heals on a fresh load —
    // auto-reload once before reporting. Recurrence falls through to the UI.
    if (recoverFromDeploySkew(error)) return
    // Log for debugging — never expose to the shopper
    console.error("[StorefrontError]", error)
    notifyBoundaryError("StorefrontError", error)
  }, [error])

  // Full reload, not Next's reset(): reset() only re-renders the segment with
  // the same client bundle, which does not recover from deploy skew (stale
  // Server Action IDs bound to form elements from a pre-deploy HTML render).
  const handleRetry = () => {
    window.location.reload()
  }

  const tErrors = useTranslations("errors")
  const tCommon = useTranslations("common")

  return (
    <div className="flex flex-col items-center justify-center min-h-[50dvh] py-16 px-4 text-center">
      <div className="h-32 w-32 text-muted-foreground mb-8">
        <TriangleAlert className="h-full w-full" strokeWidth={1} aria-hidden="true" />
      </div>
      <h2 data-functional-ui className="text-2xl font-semibold mb-4 text-balance">
        {tErrors("serverError")}
      </h2>
      <p className="text-muted-foreground mb-8 max-w-md text-pretty">
        {tErrors("serverErrorDescription")}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleRetry}
          className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {tCommon("retry")}
        </button>
        {/* Uses <a> not Next.js Link — the router may be broken in an error boundary state */}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a
          href="/"
          className="inline-flex items-center justify-center rounded-md border px-6 py-3 text-sm font-medium uppercase tracking-wide transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          {tErrors("backToHome")}
        </a>
      </div>
      {error.digest && <p className="mt-6 text-xs text-muted-foreground">#{error.digest}</p>}
    </div>
  )
}
